import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Loader2, CheckCircle, XCircle, Clock, Cpu, Search, Download, Zap, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  UPLOADED: { label: 'Subido', color: 'text-zinc-400', icon: Upload, bg: 'bg-zinc-500/10' },
  DECRYPTING: { label: 'Desencriptando...', color: 'text-blue-400', icon: Loader2, bg: 'bg-blue-500/10', spin: true },
  DECRYPTED: { label: 'Desencriptado', color: 'text-blue-400', icon: CheckCircle, bg: 'bg-blue-500/10' },
  EXTRACTING: { label: 'Extrayendo...', color: 'text-yellow-400', icon: Loader2, bg: 'bg-yellow-500/10', spin: true },
  EXTRACTED: { label: 'Extraído', color: 'text-yellow-400', icon: FileText, bg: 'bg-yellow-500/10' },
  ANALYZING: { label: 'Analizando firmware...', color: 'text-purple-400', icon: Loader2, bg: 'bg-purple-500/10', spin: true },
  MATCH_FOUND: { label: '¡Solución encontrada!', color: 'text-green-400', icon: CheckCircle, bg: 'bg-green-500/10' },
  MATCH_NOT_FOUND: { label: 'Sin solución automática', color: 'text-orange-400', icon: Search, bg: 'bg-orange-500/10' },
  READY_FOR_AUTO_APPLY: { label: 'Listo para aplicar', color: 'text-green-400', icon: Zap, bg: 'bg-green-500/10' },
  APPLYING: { label: 'Aplicando solución...', color: 'text-blue-400', icon: Loader2, bg: 'bg-blue-500/10', spin: true },
  DELIVERED: { label: 'Entregado', color: 'text-green-400', icon: Download, bg: 'bg-green-500/10' },
  FAILED: { label: 'Error', color: 'text-red-400', icon: XCircle, bg: 'bg-red-500/10' },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.UPLOADED;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
      <Icon className={`w-3.5 h-3.5 ${config.spin ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
};

const JobCard = ({ job, token, onUpdate, isAdmin }) => {
  const [expanded, setExpanded] = useState(false);
  const [queueInfo, setQueueInfo] = useState(null);
  const isProcessing = ['DECRYPTING', 'EXTRACTING', 'ANALYZING', 'APPLYING'].includes(job.status);

  // Polling for processing jobs + queue status
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(async () => {
      try {
        const [jobRes, queueRes] = await Promise.all([
          axios.get(`${API}/jobs/${job.id}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/mms/queue-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        if (queueRes?.data) setQueueInfo(queueRes.data);
        if (jobRes.data.job.status !== job.status) {
          onUpdate(jobRes.data.job);
          setQueueInfo(null);
          if (jobRes.data.job.status === 'MATCH_FOUND') toast.success('Solucion automatica encontrada!');
          if (jobRes.data.job.status === 'FAILED') toast.error(jobRes.data.job.error || 'Error en el procesamiento');
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [job.id, job.status, isProcessing, token, onUpdate]);

  const downloadBin = async () => {
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/download-bin`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = job.extracted_bin_filename || 'decrypted.bin';
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Error descargando archivo'); }
  };

  return (
    <div className={`bg-surface border rounded-md overflow-hidden ${isProcessing ? 'border-blue-500/30' : 'border-white/10'}`}>
      <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Cpu className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm truncate">{job.original_filename}</p>
          <p className="text-zinc-500 text-xs">SLAVE: {job.machine_sn} · {new Date(job.created_at).toLocaleString('es-ES')}</p>
        </div>
        <StatusBadge status={job.status} />
        {isProcessing && queueInfo && queueInfo.queue_length > 0 && (
          <span className="text-orange-400 text-xs font-mono px-2 py-0.5 bg-orange-500/10 rounded" data-testid={`queue-pos-${job.id}`}>
            Cola: {queueInfo.queue_length}
          </span>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-4 space-y-3">
          {/* Progress steps */}
          <div className="flex gap-1">
            {['UPLOADED', 'DECRYPTING', 'EXTRACTED', 'ANALYZING', job.status === 'MATCH_FOUND' ? 'MATCH_FOUND' : 'MATCH_NOT_FOUND'].map((step, i) => {
              const steps = ['UPLOADED', 'DECRYPTED', 'EXTRACTED', 'ANALYZING', 'MATCH_FOUND', 'MATCH_NOT_FOUND', 'DELIVERED'];
              const currentIdx = steps.indexOf(job.status);
              const stepIdx = i;
              const active = stepIdx <= currentIdx || job.status === 'DELIVERED';
              return <div key={i} className={`h-1.5 flex-1 rounded-full ${active ? 'bg-primary' : 'bg-white/10'}`} />;
            })}
          </div>

          {/* Analysis results */}
          {job.analysis && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'ECU', value: job.analysis.ecu_family_hint?.value },
                { label: 'HW', value: job.analysis.hw_id?.value },
                { label: 'SW', value: job.analysis.sw_id?.value },
                { label: 'OEM', value: job.analysis.oem_hint?.value },
              ].filter(i => i.value).map((item, idx) => (
                <div key={idx} className="bg-surface-highlight rounded px-3 py-2">
                  <span className="text-zinc-500 text-xs">{item.label}</span>
                  <span className="text-white text-xs font-mono block truncate">{item.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Match info */}
          {job.status === 'MATCH_FOUND' && (
            <div className="bg-green-500/5 border border-green-500/20 rounded p-3">
              <p className="text-green-400 text-sm font-medium">Coincidencia: {job.match_type} ({Math.round((job.match_confidence || 0) * 100)}%)</p>
              {job.match_vehicle && <p className="text-zinc-400 text-xs">{job.match_vehicle} · {job.match_ecu}</p>}
            </div>
          )}

          {/* Error */}
          {job.error && (
            <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
              <p className="text-red-400 text-sm">{job.error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {job.extracted_bin_id && isAdmin && (
              <Button size="sm" onClick={downloadBin} className="bg-primary hover:bg-primary/90 text-xs" data-testid={`download-bin-${job.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> Descargar .bin
              </Button>
            )}
            {job.extracted_bin_id && !isAdmin && (
              <span className="text-zinc-500 text-xs flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5 text-green-500" /> Archivo procesado - Solo admin puede descargar
              </span>
            )}
          </div>

          {/* Audit log */}
          {job.audit_log && (
            <div className="text-xs text-zinc-600 space-y-0.5 pt-2 border-t border-white/5">
              {job.audit_log?.slice(-5).map((entry, i) => (
                <p key={i}>{new Date(entry.timestamp).toLocaleTimeString('es-ES')} — {entry.action} {entry.details && `(${entry.details})`}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FlexJobsPage = () => {
  const { user, token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTool, setSelectedTool] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [file, setFile] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(res.data.jobs || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchJobs();
    axios.get(`${API}/mms/user-tools`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTools(r.data.tools || []))
      .catch(() => {});
  }, [token, fetchJobs]);

  const handleJobUpdate = useCallback((updatedJob) => {
    setJobs(prev => prev.map(j => j.id === updatedJob.id ? updatedJob : j));
  }, []);

  const submitJob = async (e) => {
    e.preventDefault();
    if (!selectedTool || !file) return toast.error('Selecciona herramienta SLAVE y archivo');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('machine_sn', selectedTool);
      fd.append('vehicle_id', vehicleId || 'AUTO');
      const res = await axios.post(`${API}/jobs`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message || 'Job creado');
      setFile(null); setVehicleId('');
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando job');
    } finally {
      setSubmitting(false);
    }
  };

  const userTools = tools.filter(t => user?.is_admin || t.user_id === user?.id);
  const userOps = user?.is_admin ? '∞' : (user?.mms_operations || 0);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-3xl font-bold text-white">FLEX DECRYPT</h1>
          <span className="text-zinc-400 text-sm">Operaciones: <span className="text-primary font-bold">{userOps}</span></span>
        </div>

        {/* New Job Form */}
        <form onSubmit={submitJob} className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" /> NUEVO TRABAJO
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Herramienta SLAVE <span className="text-red-400">*</span></Label>
              <Select value={selectedTool} onValueChange={setSelectedTool}>
                <SelectTrigger className="bg-surface-highlight border-white/10 text-white h-11">
                  <SelectValue placeholder="Seleccionar SLAVE..." />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  {userTools.map(t => (
                    <SelectItem key={t.machine_sn || t.serial_number || t.id} value={t.machine_sn || t.serial_number || t.sn} className="text-white hover:bg-white/10">
                      {t.label || t.tool_name || t.machine_sn || 'SLAVE'} — {t.machine_sn || t.serial_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">ID Vehículo / Matrícula</Label>
              <Input value={vehicleId} onChange={e => setVehicleId(e.target.value)}
                placeholder="Ej: 1234ABC" className="bg-surface-highlight border-white/10 text-white h-11" />
            </div>
          </div>
          <div className="space-y-2 mb-4">
            <Label className="text-zinc-300">Archivo encriptado FLEX <span className="text-red-400">*</span></Label>
            <label className={`flex items-center justify-center border-2 border-dashed rounded-md p-8 cursor-pointer transition-colors ${file ? 'border-primary/40 bg-primary/5' : 'border-white/10 hover:border-white/20'}`}>
              <input type="file" className="hidden" accept=".mmf"
                onChange={e => e.target.files?.[0] && setFile(e.target.files[0])} />
              {file ? (
                <div className="text-center">
                  <FileText className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">{file.name}</p>
                  <p className="text-zinc-500 text-xs">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">Arrastra o haz clic para subir</p>
                  <p className="text-zinc-600 text-xs">Solo archivos .mmf</p>
                </div>
              )}
            </label>
          </div>
          <Button type="submit" disabled={submitting || !selectedTool || !file}
            className="w-full bg-primary hover:bg-primary/90 font-heading uppercase h-12">
            {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Procesando...</> : <><Zap className="w-5 h-5 mr-2" /> Desencriptar con FLEX</>}
          </Button>
        </form>

        {/* Jobs List */}
        <h2 className="font-heading text-lg font-bold text-white mb-3">MIS TRABAJOS</h2>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">No hay trabajos todavía</p>
        ) : (
          <div className="space-y-2">
            {jobs.map(job => (
              <JobCard key={job.id} job={job} token={token} onUpdate={handleJobUpdate} isAdmin={user?.is_admin} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FlexJobsPage;
